import $ from 'jquery';
import ShopifySurfacePickUp from '@pixelunion/shopify-surface-pick-up';
import EventHandler from '@pixelunion/events';
import RecipientForm from '@pixelunion/pxs-gift-card-recipient-form';
import Forms from '../Forms';
import AddToCartFlyout from './AddToCartFlyout';
import Modal from './Modal';
import MessageBanner from './MessageBanner';
import QuantitySelector from '../helpers/QuantitySelector';
import { setupRippleEffect } from '../helpers/Ripple';

export default class ProductDetails {
  constructor(options) {
    this.$window = $(window);
    this.$formArea = options.$formArea;
    this.$details = options.$details;
    this.context = options.context;
    this.settings = options.settings;
    this.product = options.product;
    this.sectionId = options.sectionId;
    this.el = options.productEl;
    this.events = new EventHandler();
    this.url = window.location.search;
    this.urlParams = new URLSearchParams(this.url);
    this.picker = this.el.querySelector('kit-variant-picker');
    this.select_first_available_variant = this.settings.select_first_available_variant;
    this.recipientFormEl = this.el.querySelector('[data-recipient-form]');
    this.surfacePickUpEl = this.el.querySelector('[data-surface-pick-up]');
    this.requestFormEl = this.el.querySelector('[data-product-request-form]');
    this.gallery = options.gallery || this.el.querySelector('product-gallery');

    if (this.recipientFormEl) {
      this.recipientForm = new RecipientForm(this.el);
    }

    if (this.surfacePickUpEl) {
      this.surfacePickUp = new ShopifySurfacePickUp(this.surfacePickUpEl);
    }
    if (Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }

    if (this.requestFormEl) {
      this.requestFormSKU = this.requestFormEl.querySelector('[data-request-form-sku]');
      this.requestFormVariant = this.requestFormEl.querySelector('[data-request-form-variant]');
      this.requestFormVariantID = this.requestFormEl.querySelector('[data-request-form-variant-id]');

      new Forms(this.requestFormEl);

      // eslint-disable-next-line max-len
      if (!this.select_first_available_variant && this.requestFormVariant && this.requestFormVariantID) {
        this.requestFormVariant.removeAttribute('name');
        this.requestFormVariantID.removeAttribute('name');
      }

      this.events.register(this.requestFormEl, 'submit', () => {
        const message = this.requestFormEl.querySelector('[data-product-request-form-message]');

        if (message.value === '') {
          message.classList.add('form-field-filled');
          message.innerHTML = this.context.request_message_empty;
        }
      });

      this.events.register(window, 'load', () => {
        if (this.urlParams.get('contact_posted')) {
          this.requestFormEl.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    }

    if (!$(this.$formArea).length) return;

    this.isDefaultVariant = !this.picker;

    if (this.surfacePickUp) {
      const variant = this._selectedVariant();
      this.surfacePickUp.load(variant ? variant.id : null);
    }

    if (this.picker) {
      this.events.register(
        document,
        'shopify:product:select',
        event => this._onProductSelect(event),
      );

      this.events.register(
        this.picker,
        'kit:variant-picker:after-change',
        event => this._onPickerAfterChange(event),
      );

      this._updateBadge(this._selectedVariant());
    }

    this.modal = new Modal({
      onClose: () => this.modal.unload(),
      modalId: 1,
    });

    if (this.surfacePickUp) {
      this.surfacePickUp.onModalRequest(contents => {
        let container = this.$formArea[0].querySelector('[data-surface-pick-up-modal-contents]');
        const variant = this._selectedVariant();

        // The widget only loads for a resolved variant, but the picker can now
        // legitimately report none while a selection is incomplete.
        if (!variant) return;

        const variantTitle = !this.isDefaultVariant ? `<div class="surface-pick-up-modal__variant">${variant.title}</div>` : '';

        const modalContents = `
          <div class="surface-pick-up-modal__header">
            <h2 class="surface-pick-up-modal__title">${this.product.title}</h2>
            ${variantTitle}
          </div>
          ${contents}
          `;

        if (!container) {
          container = document.createElement('div');
          container.setAttribute('data-surface-pick-up-modal-contents', '');
          container.style.display = 'none';
          container.innerHTML = modalContents;
          this.$formArea[0].appendChild(container);
        } else {
          container.innerHTML = modalContents;
        }

        this.modal.open('[data-surface-pick-up-modal-contents]', 'surface-pick-up');
      });
    }

    this.addToCartFlyout = null;

    this.atcCallbacks = options.atcCallbacks;

    // Form
    this.$form = this.$formArea.find('[data-product-form]');
    this.detailsLink = this.$formArea[0].querySelector('[data-product-details-link]');
    this.quantitySelector = this.el.querySelector('[data-quantity-selector]');

    if (this.detailsLink) {
      this.detailsBaseHref = this.detailsLink.getAttribute('href');
    }

    if (this.quantitySelector) {
      this.productQuantityBox = new QuantitySelector({
        quantityField: this.quantitySelector,
      });
    }

    this.forms = new Forms(this.$form);

    if (this.product) {
      this._bindEvents();
    }
  }

  /**
   * Looked up live, never cached: the button is a `data-kit-refresh` region, so
   * kit-section-refresh replaces the element on every variant change and any
   * held reference would be detached from the document.
   */
  get $productAtcButton() {
    return this.$formArea.find('[data-product-atc]');
  }

  unload() {
    if (this.$form) {
      this.$form.off(`.product-details-${this.sectionId}`);
    }

    if (this.forms) {
      this.forms.unload();
    }

    if (this.messageBanner) {
      this.messageBanner.unload();
    }

    this.events.unregisterAll();
  }

  _bindEvents() {
    this.$form.on(`submit.product-details-${this.sectionId}`, event => this._addToCartFlyout(event));
  }

  /**
   * The variant the picker last resolved on the server. Null while a selection
   * is incomplete, or when the product has no options.
   */
  _selectedVariant() {
    const blob = (this.picker || this.el).querySelector('[data-kit-selected-variant]');

    if (!blob) return null;

    try {
      return JSON.parse(blob.textContent.trim() || 'null');
    } catch (error) {
      console.error('Could not parse the selected variant JSON.', error);
      return null;
    }
  }

  // Handles change effects not covered by kit.section-refresh
  _onProductSelect(event) {
    if (event.target !== this.picker) return;

    // Block ATC in flight, without the .disabled class's sold-out styling
    this.$productAtcButton.prop('disabled', true);

    // Resolve the variant first, then do the DOM work outside the catch — a
    // failure to render must not be reported as a failed variant change, and
    // must not trigger the unavailable path.
    event.promise
      .then(
        () => this._selectedVariant(),
        error => {
          if (error && error.name === 'AbortError') throw error;

          console.error('Variant change failed:', error);
          return null;
        },
      )
      .then(variant => this._switchVariant(variant))
      .catch(error => {
        if (error && error.name === 'AbortError') return;

        console.error('Could not apply the variant change:', error);
      });
  }

  // Combined listings: load the selected product's page rather than updating in place
  _onPickerAfterChange(event) {
    if (event.target !== this.picker || !event.detail.productChanged) return;

    const productUrl = new URL(this.picker.dataset.productUrl, window.location.origin);
    const targetUrl = window.location.pathname.includes('/products/')
      ? new URL(window.location.href)
      : productUrl;

    targetUrl.pathname = productUrl.pathname;

    if (this.picker.dataset.variantId) {
      targetUrl.searchParams.set('variant', this.picker.dataset.variantId);
    }

    window.location.assign(targetUrl.toString());
  }

  _switchVariant(variant) {
    setupRippleEffect(this.el);

    this._updateBadge(variant);

    if (variant && variant.featured_media && typeof this.gallery?.showVariantMedia === 'function') {
      this.gallery.showVariantMedia(variant);
    }

    if (this.surfacePickUp) {
      this.surfacePickUp.load(variant ? variant.id : null);
    }

    this._updateFullDetailsLink(variant);

    if (Shopify.PaymentButton) {
      Shopify.PaymentButton.init();

      const paymentButton = this.el.querySelector('dynamic-checkout');

      if (paymentButton) {
        if (variant && variant.available) {
          paymentButton.removeAttribute('disabled');
        } else {
          paymentButton.setAttribute('disabled', 'disabled');
        }
      }
    }

    if (this.requestFormEl && !this.urlParams.get('contact_posted')) {
      this.requestFormVariant.value = variant ? variant.title : '';
      this.requestFormVariantID.value = variant ? variant.id : '';
      this.requestFormSKU.value = variant ? variant.sku : '';

      if (this.requestFormSKU.value === '') {
        this.requestFormSKU.removeAttribute('name');
      } else {
        this.requestFormSKU.setAttribute('name', 'contact[sku]');
      }

      if (!this.select_first_available_variant) {
        this.requestFormVariant.setAttribute('name', 'contact[variant]');
        this.requestFormVariantID.setAttribute('name', 'contact[id]');
      }
    }
  }

  /**
   * The sale badge renders a range (max savings across variants) and a hidden
   * single-variant copy. Show the one matching the selection. Looked up live,
   * since the price region is replaced by kit-section-refresh.
   */
  _updateBadge(variant = null) {
    const $badge = $(this.el).find('[data-product-pricing] [data-badge-sales]');

    if (!$badge.length) return;

    const $badgeRange = $badge.find('[data-badge-sales-range]');
    const $badgeSingle = $badge.find('[data-badge-sales-single]');

    if (!variant) {
      $badgeSingle.toggle(false);
      $badgeRange.toggle(true);
      $badge.toggle(true);
      return;
    }

    const priceSaved = variant.compare_at_price ? variant.compare_at_price - variant.price : 0;

    if (priceSaved <= 0) {
      $badge.toggle(false);
      return;
    }

    $badgeRange.toggle(false);
    $badgeSingle.toggle(true);
    $badge.toggle(true);

    const $badgeMoneySaved = $badgeSingle.find('[data-price-money-saved]');
    const $badgePercentSaved = $badgeSingle.find('[data-price-percent-saved]');

    if ($badgeMoneySaved.length) {
      $badgeMoneySaved.text(Shopify.formatMoney(priceSaved, this.settings.money_format));
    }

    if ($badgePercentSaved.length) {
      $badgePercentSaved.text(Math.round((priceSaved * 100) / variant.compare_at_price));
    }
  }

  _updateFullDetailsLink(variant) {
    if (!this.detailsLink) return;

    this.detailsLink.setAttribute(
      'href',
      variant ? `${this.detailsBaseHref}?variant=${variant.id}` : this.detailsBaseHref,
    );
  }

  _addToCartFlyout(event) {
    event.preventDefault();

    if (this.recipientFormEl) {
      this.recipientFormEl.classList.remove('recipient-form--has-errors');
    }

    const variant = this._selectedVariant();

    if (!variant) {
      this.messageBanner = new MessageBanner(this.context.select_variant, 'error');
      return;
    }

    if (this.addToCartFlyout) {
      this.addToCartFlyout.unload();
    }

    const formData = this.$form.serializeArray();
    const options = {
      atcButton: this.$productAtcButton[0],
      settings: {
        moneyFormat: this.settings.money_format,
        cartRedirection: this.settings.cart_redirection,
      },
    };
    this.addToCartFlyout = new AddToCartFlyout(formData, options, this.atcCallbacks);
  }
}
